import { ISelectItem } from "@components/inputs/select/Select.types";
import { SwitchType } from "@components/inputs/switch/Switch";
import { ifAll, ifAny, IGetValueArgs, not } from "@components/smart/FieldInfo";
import { countryDef, withDisplayName } from "@components/smart/GeneralFieldDefinition";
import { FilterBarGroup, getDefaultFilterGroupDef } from "@components/smart/smartFilterBar/SmartFilterBar.types";
import { IFormGroupDef } from "@components/smart/smartFormGroup/SmartFormGroup";
import { ISummaryItem } from "@components/smart/smartSummaryItem/SmartSummaryItem";
import { TSmartODataTableStorage } from "@components/smart/smartTable/SmartODataTableBase";
import { ICellValueObject, TCellValue } from "@components/table";
import { createPath } from "@odata/BindingContext";
import {
    EntitySetName,
    EntityTypeName,
    IPrTaxpayerDeclarationEntity,
    PrAttendanceEntity,
    PrEmployeeEntity,
    PrOtherProviderAddressEntity,
    PrOtherProviderEmployerAddressEntity,
    PrTaxpayerDeclarationChildEntity,
    PrTaxpayerDeclarationDiscountEntity,
    PrTaxpayerDeclarationEntity,
    PrTaxpayerDeclarationOtherProviderEntity
} from "@odata/GeneratedEntityTypes";
import { PrTaxDiscountTypeCode, PrTaxpayerDeclarationStatusCode } from "@odata/GeneratedEnums";
import { IFormatOptions } from "@odata/OData.utils";
import { universalPersonNameFormatter } from "@pages/admin/users/Users.utils";
import { userNameWithAvatarFormatter } from "@pages/admin/users/UsersDef";
import {
    getNumberOursSummaryDef,
    getNumberRangeFieldDefs,
    NumberRangeAdditionalProperties
} from "@pages/numberRange/NumberRange.utils";
import { employeeSelectDef } from "@pages/payroll/employee/Employee.utils";
import {
    disabilityDiscountType,
    getPathsForDeductionType,
    getTogglePropPath,
    hasDisabilityDiscount,
    isThereOtherProviderPath,
    showChildrenPath,
} from "@pages/payroll/taxpayerDeclaration/TaxpayerDeclaration.utils";
import TaxpayerDeclarationFormView, {
    ITaxPayerDeclarationCustomData
} from "@pages/payroll/taxpayerDeclaration/TaxpayerDeclarationFormView";
import { isNewEntity } from "@utils/definition.utils";
import { arrayInsert } from "@utils/general";
import i18next from "i18next";
import React from "react";
import { DefaultTheme } from "styled-components";

import { DASH_CHARACTER } from "../../../constants";
import { BasicInputSizes, FieldType, GroupedField, Sort, TextAlign } from "../../../enums";
import { ColoredText } from "../../../global.style";
import { TValue } from "../../../global.types";
import DateType, { DATE_MAX, DATE_MIN, getUtcDate, getUtcDayjs } from "../../../types/Date";
import { IFormDef } from "../../../views/formView/Form";
import { TableButtonsAction } from "../../../views/table/TableToolbar.utils";
import { ISplitPageTableDef } from "../../../views/table/TableView.utils";
import { setDefByEntityType } from "../../getDefByEntityType";
import { IDefinition, IGetDefinition, TFieldsDefinition } from "../../PageUtils";

const switchDef = {
    type: FieldType.Switch,
    fieldSettings: {
        type: SwitchType.YesNo,
    },
    customizationData: {
        useForCustomization: false
    }
};

const isVisibleForDiscountType = (type: PrTaxDiscountTypeCode) => (args: IGetValueArgs) => {
    const discountType = (args.storage.getCustomData() as ITaxPayerDeclarationCustomData).discountTypes[type];
    const year = args.storage.data.entity.Year;
    if (discountType && year) {
        return getUtcDayjs().set("year", year).isBetween(discountType.DateValidFrom ?? DATE_MIN, discountType.DateValidTo ?? DATE_MAX, "year", "[]");
    }
    return true;
};

const getGroupForDeductionType = (type: PrTaxDiscountTypeCode): IFormGroupDef => {
    return {
        id: type,
        isVisible: isVisibleForDiscountType(type),
        title: i18next.t(`TaxpayerDeclaration:Types.${type}`),
        togglePropPath: getTogglePropPath(type),
        isSmallBottomMargin: true,
        customizationData: {
            isLocked: true
        },
        rows: []
    };
};

const getFieldDefinitions = () => {
    return Object.values(PrTaxDiscountTypeCode).reduce((defs: TFieldsDefinition, type) => {
        const { switchPath } = getPathsForDeductionType(type);

        defs[switchPath] = {
            ...switchDef
        };
        return defs;
    }, {});
};

const getThemeColorForDeclarationStatus = (status: PrTaxpayerDeclarationStatusCode): keyof DefaultTheme => {
    switch (status) {
        case PrTaxpayerDeclarationStatusCode.Signed:
            return "C_SEM_text_good";
        case PrTaxpayerDeclarationStatusCode.Draft:
            return "C_SEM_text_bad";
        case PrTaxpayerDeclarationStatusCode.WaitingForSignature:
            return "C_SEM_text_warning";
        case PrTaxpayerDeclarationStatusCode.Ended:
            return "C_TEXT_primary";
    }
};

const isEmployed = (args: IGetValueArgs): boolean => {
    return args.storage.getValueByPath(createPath(PrTaxpayerDeclarationEntity.OtherProvider, PrTaxpayerDeclarationOtherProviderEntity.IsEmployed));
};

const isThereOtherProvider = (args: IGetValueArgs): boolean => {
    return args.storage.getValueByPath(isThereOtherProviderPath);
};

export const getDefinitions: IGetDefinition = (context, isSingleEmployeeView?: boolean): IDefinition => {
    const table: ISplitPageTableDef = {
        id: `${EntityTypeName.PrTaxpayerDeclaration}Table`,
        filter: (args: IGetValueArgs) => {
            if (isSingleEmployeeView) {
                const employeeId = (args.storage as TSmartODataTableStorage).getCustomData().rootStorage.data.bindingContext.getKey();
                return `${PrTaxpayerDeclarationEntity.Employee}/Id eq ${employeeId}`;
            }
            return "";
        },
        filterBarDef: [{
            ...getDefaultFilterGroupDef(FilterBarGroup.Filters),
            isValueHelp: true,
            defaultFilters: [
                PrTaxpayerDeclarationEntity.NumberOurs,
                PrTaxpayerDeclarationEntity.Status,
                PrTaxpayerDeclarationEntity.Employee,
                PrTaxpayerDeclarationEntity.DateSigned,
                PrTaxpayerDeclarationEntity.DateEnded
            ],
            filterDefinition: {
                [PrTaxpayerDeclarationEntity.NumberOurs]: {},
                ...withDisplayName(PrTaxpayerDeclarationEntity.Status),
                ...withDisplayName(PrTaxpayerDeclarationEntity.Employee, PrEmployeeEntity.LastName, {
                    formatter: (val: TValue, args: IFormatOptions) => {
                        return universalPersonNameFormatter((args.entity as IPrTaxpayerDeclarationEntity).Employee);
                    },
                    additionalProperties: [
                        { id: PrEmployeeEntity.FirstName }
                    ]
                }),
                [PrTaxpayerDeclarationEntity.DateSigned]: {},
                [PrTaxpayerDeclarationEntity.DateEnded]: {},
            }
        }],
        columns: [
            PrTaxpayerDeclarationEntity.NumberOurs,
            PrTaxpayerDeclarationEntity.Status,
            PrTaxpayerDeclarationEntity.DateSigned,
            PrTaxpayerDeclarationEntity.DateEnded
        ],
        columnDefinition: {
            [PrTaxpayerDeclarationEntity.NumberOurs]: {},
            [PrTaxpayerDeclarationEntity.Status]: {
                textAlign: TextAlign.Center,
                formatter: (val: TValue, args: IFormatOptions): TCellValue => {
                    const entity = args.entity as IPrTaxpayerDeclarationEntity;
                    return {
                        value: <ColoredText
                                color={getThemeColorForDeclarationStatus(entity.Status?.Code as PrTaxpayerDeclarationStatusCode)}>
                            {entity.Status?.Name}
                        </ColoredText>,
                        tooltip: entity.Status?.Name
                    };
                }
            },
            [PrTaxpayerDeclarationEntity.DateSigned]: {},
            [PrTaxpayerDeclarationEntity.DateEnded]: {},
        },
        preventStoreVariant: isSingleEmployeeView,
        title: i18next.t("TaxpayerDeclaration:Title"),
        initialSortBy: [{ id: PrTaxpayerDeclarationEntity.NumberOurs, sort: Sort.Asc }],
        tabs: [],
        tabsSettings: {
            customFilter: (selectedTab: string) => {
                if (!selectedTab) {
                    return "";
                }
                return `${PrTaxpayerDeclarationEntity.Year} eq ${selectedTab} AND ${PrAttendanceEntity.Month}`;
            },
            withoutCounts: true
        }
    };

    // edit def for table view on employee page
    if (isSingleEmployeeView) {
        table.columns = arrayInsert(table.columns, PrTaxpayerDeclarationEntity.Year, 0);
        table.columnDefinition[PrTaxpayerDeclarationEntity.Year] = {
            formatter: val => val?.toString()
        };
    } else {
        table.columns = arrayInsert(table.columns, PrTaxpayerDeclarationEntity.Employee, 1);
        table.columnDefinition[PrTaxpayerDeclarationEntity.Employee] = {
            textAlign: TextAlign.Left,
            formatter: (val: TValue, args: IFormatOptions) => {
                return userNameWithAvatarFormatter(val, {
                    ...args,
                    entity: (args.entity as IPrTaxpayerDeclarationEntity).Employee
                });
            },
            additionalProperties: [
                { id: PrEmployeeEntity.FirstName },
                { id: PrEmployeeEntity.LastName }
            ]
        };
    }

    const summary: ISummaryItem[] = [{
        id: PrTaxpayerDeclarationEntity.Employee,
        formatter: (val: TValue, args: IFormatOptions) => {
            return (userNameWithAvatarFormatter(val, {
                ...args,
                entity: (args.entity as IPrTaxpayerDeclarationEntity).Employee
            }, { isBig: true }) as ICellValueObject)?.value;
        },
        additionalProperties: [
            { id: PrEmployeeEntity.FirstName },
            { id: PrEmployeeEntity.LastName }
        ],
        isVisible: not(isNewEntity)
    },
        {
            ...getNumberOursSummaryDef("TaxpayerDeclaration")
        },
        {
            id: PrTaxpayerDeclarationEntity.Status,
            label: i18next.t("TaxpayerDeclaration:Form.Status"),
            formatter: (val: TValue, args: IFormatOptions) => {
                const entity = args.entity as IPrTaxpayerDeclarationEntity;
                return <ColoredText
                        color={getThemeColorForDeclarationStatus(entity.Status?.Code as PrTaxpayerDeclarationStatusCode)}>
                    {entity.Status?.Name}
                </ColoredText>;
            },
            isVisible: not(isNewEntity)
        },
        {
            id: PrTaxpayerDeclarationEntity.DateSigned,
            formatter: (val: TValue) => {
                return val ? DateType.localFormat(val as Date) : DASH_CHARACTER;
            },
            type: FieldType.Date,
            label: i18next.t("TaxpayerDeclaration:Form.DateSigned"),
            isVisible: args => {
                return args.storage.data.entity.Status?.Code === PrTaxpayerDeclarationStatusCode.Signed || args.storage.data.entity.Status?.Code === PrTaxpayerDeclarationStatusCode.Ended;
            }
        },
        {
            id: PrTaxpayerDeclarationEntity.DateEnded,
            formatter: (val: TValue) => {
                return val ? DateType.localFormat(val as Date) : DASH_CHARACTER;
            },
            label: i18next.t("TaxpayerDeclaration:Form.DateInvalidated"),
            isVisible: args => {
                return args.storage.data.entity.Status?.Code === PrTaxpayerDeclarationStatusCode.Ended;
            }
        }
    ];

    const form: IFormDef = {
        id: `${EntityTypeName.PrTaxpayerDeclaration}Form`,
        translationFiles: getDefinitions.translationFiles,
        formControl: TaxpayerDeclarationFormView,
        isDeletable: true,
        isReadOnly: args => [PrTaxpayerDeclarationStatusCode.Signed, PrTaxpayerDeclarationStatusCode.Ended].includes(args.storage.data.entity.Status?.Code),
        summary,
        customHeaderActions: [{
            id: TableButtonsAction.MassExport,
            label: i18next.t("TaxpayerDeclaration:MassExportPdf.TitleSingle"),
            iconName: "ExportPdf",
            ignoreMasterDisabled: true,
            isVisible: args => !args.storage.data.bindingContext.isNew()
        }],
        title: i18next.t("TaxpayerDeclaration:FormTitle"),
        additionalProperties: [{
            id: PrTaxpayerDeclarationEntity.Children
        }, {
            id: PrTaxpayerDeclarationEntity.Discounts,
            additionalProperties: [{
                id: PrTaxpayerDeclarationDiscountEntity.DiscountType
            }]

        }, ...NumberRangeAdditionalProperties],
        fieldDefinition: {
            ...getNumberRangeFieldDefs("TaxpayerDeclaration"),
            ...getFieldDefinitions(),
            [showChildrenPath]: {
                ...switchDef
            },
            [hasDisabilityDiscount]: {
                ...switchDef
            },
            [PrTaxpayerDeclarationEntity.Year]: {
                label: i18next.t("TaxpayerDeclaration:Form.ForYear"),
                type: FieldType.ComboBox,
                defaultValue: getUtcDate().getFullYear(),
                fieldSettings: {
                    items: [{
                        id: getUtcDate().getFullYear(),
                        label: getUtcDate().getFullYear().toString()
                    }, {
                        id: getUtcDate().getFullYear() - 1,
                        label: (getUtcDate().getFullYear() - 1).toString()
                    }]
                },
                isVisible: isNewEntity
            },
            [createPath(PrTaxpayerDeclarationEntity.Children, PrTaxpayerDeclarationChildEntity.FirstName)]: {},
            [createPath(PrTaxpayerDeclarationEntity.Children, PrTaxpayerDeclarationChildEntity.LastName)]: {},
            [createPath(PrTaxpayerDeclarationEntity.Children, PrTaxpayerDeclarationChildEntity.PersonalIdentificationNumber)]: {
                width: BasicInputSizes.S
            },
            ...withDisplayName(createPath(PrTaxpayerDeclarationEntity.Children, PrTaxpayerDeclarationChildEntity.Order), {
                type: FieldType.ComboBox,
                width: BasicInputSizes.S
            }),
            [createPath(PrTaxpayerDeclarationEntity.Children, PrTaxpayerDeclarationChildEntity.IsDisabilityDiscount)]: {
                type: FieldType.ComboBox,
                width: BasicInputSizes.XS,
                defaultValue: false,
                fieldSettings: {
                    items: [{
                        id: false,
                        label: i18next.t("Common:General.No")
                    }, {
                        id: true,
                        label: i18next.t("Common:General.Yes")
                    }]
                }
            },
            [disabilityDiscountType]: {
                type: FieldType.ComboBox,
                label: i18next.t("TaxpayerDeclaration:Form.DiscountType"),
                isRequired: true,
                isVisible: args => {
                    return !!args.storage.getValueByPath(hasDisabilityDiscount);
                },
                fieldSettings: {
                    items: [{
                        id: PrTaxDiscountTypeCode.BasicDisabilityDiscount,
                        label: i18next.t("TaxpayerDeclaration:Types.Basic")
                    }, {
                        id: PrTaxDiscountTypeCode.ExtendedDisabilityDiscount,
                        label: i18next.t("TaxpayerDeclaration:Types.Extended")
                    }],
                    itemsForRender: (items: ISelectItem[]) => {
                        return items?.filter(item => isVisibleForDiscountType(item.id as PrTaxDiscountTypeCode));
                    }
                },
                defaultValue: PrTaxDiscountTypeCode.BasicDisabilityDiscount
            },
            [isThereOtherProviderPath]: {
                type: FieldType.Switch,
                label: i18next.t("TaxpayerDeclaration:Form.IsThereOtherProvider"),
                fieldSettings: { type: SwitchType.YesNo },
                defaultValue: false
            },
            [createPath(PrTaxpayerDeclarationEntity.OtherProvider, PrTaxpayerDeclarationOtherProviderEntity.FirstName)]: {
                isVisible: isThereOtherProvider
            },
            [createPath(PrTaxpayerDeclarationEntity.OtherProvider, PrTaxpayerDeclarationOtherProviderEntity.LastName)]: {
                isVisible: isThereOtherProvider
            },
            [createPath(PrTaxpayerDeclarationEntity.OtherProvider, PrTaxpayerDeclarationOtherProviderEntity.PersonalIdentificationNumber)]: {
                width: BasicInputSizes.S,
                isVisible: isThereOtherProvider
            },
            [createPath(PrTaxpayerDeclarationEntity.OtherProvider, PrTaxpayerDeclarationOtherProviderEntity.Address, PrOtherProviderAddressEntity.Street)]: {
                isVisible: isThereOtherProvider
            },
            [createPath(PrTaxpayerDeclarationEntity.OtherProvider, PrTaxpayerDeclarationOtherProviderEntity.Address, PrOtherProviderAddressEntity.City)]: {
                groupedField: GroupedField.MultiStart,
                isVisible: isThereOtherProvider
            },
            [createPath(PrTaxpayerDeclarationEntity.OtherProvider, PrTaxpayerDeclarationOtherProviderEntity.Address, PrOtherProviderAddressEntity.PostalCode)]: {
                width: BasicInputSizes.S,
                groupedField: GroupedField.MultiEnd,
                isVisible: isThereOtherProvider
            },
            [createPath(PrTaxpayerDeclarationEntity.OtherProvider, PrTaxpayerDeclarationOtherProviderEntity.Address, PrOtherProviderAddressEntity.Country)]: {
                ...countryDef,
                isVisible: isThereOtherProvider
            },
            [createPath(PrTaxpayerDeclarationEntity.OtherProvider, PrTaxpayerDeclarationOtherProviderEntity.IsApplyingTaxBenefits)]: {
                type: FieldType.Switch,
                fieldSettings: { type: SwitchType.YesNo },
                isVisible: isThereOtherProvider
            },
            [createPath(PrTaxpayerDeclarationEntity.OtherProvider, PrTaxpayerDeclarationOtherProviderEntity.IsEmployed)]: {
                type: FieldType.Switch,
                fieldSettings: { type: SwitchType.YesNo },
                isVisible: isThereOtherProvider
            },
            [createPath(PrTaxpayerDeclarationEntity.OtherProvider, PrTaxpayerDeclarationOtherProviderEntity.EmployerName)]: {
                isVisible: ifAll(isEmployed, isThereOtherProvider)
            },
            [createPath(PrTaxpayerDeclarationEntity.OtherProvider, PrTaxpayerDeclarationOtherProviderEntity.EmployerAddress, PrOtherProviderEmployerAddressEntity.Street)]: {
                isVisible: ifAll(isEmployed, isThereOtherProvider)
            },
            [createPath(PrTaxpayerDeclarationEntity.OtherProvider, PrTaxpayerDeclarationOtherProviderEntity.EmployerAddress, PrOtherProviderEmployerAddressEntity.City)]: {
                groupedField: GroupedField.MultiStart,
                isVisible: ifAll(isEmployed, isThereOtherProvider)
            },
            [createPath(PrTaxpayerDeclarationEntity.OtherProvider, PrTaxpayerDeclarationOtherProviderEntity.EmployerAddress, PrOtherProviderEmployerAddressEntity.PostalCode)]: {
                width: BasicInputSizes.S,
                groupedField: GroupedField.MultiEnd,
                isVisible: ifAll(isEmployed, isThereOtherProvider)
            },
            [createPath(PrTaxpayerDeclarationEntity.OtherProvider, PrTaxpayerDeclarationOtherProviderEntity.EmployerAddress, PrOtherProviderEmployerAddressEntity.Country)]: {
                ...countryDef,
                isVisible: ifAll(isEmployed, isThereOtherProvider)
            },
        },
        groups: [
            getGroupForDeductionType(PrTaxDiscountTypeCode.TaxPayerDiscount),
            {
                id: PrTaxpayerDeclarationEntity.Children,
                title: i18next.t("TaxpayerDeclaration:Types.Children"),
                togglePropPath: showChildrenPath,
                showGroupDivider: false,
                isSmallBottomMargin: true,
                lineItems: {
                    additionalFields: [],
                    collection: PrTaxpayerDeclarationEntity.Children,
                    order: "Id",
                    canAdd: args => args.storage.getValueByPath(showChildrenPath),
                    isItemVisible: args => args.storage.getValueByPath(showChildrenPath),
                    columns: [
                        { id: PrTaxpayerDeclarationChildEntity.FirstName },
                        { id: PrTaxpayerDeclarationChildEntity.LastName },
                        { id: PrTaxpayerDeclarationChildEntity.PersonalIdentificationNumber },
                        { id: PrTaxpayerDeclarationChildEntity.Order },
                        { id: PrTaxpayerDeclarationChildEntity.IsDisabilityDiscount }
                    ]
                }
            }, {
                id: PrTaxpayerDeclarationEntity.OtherProvider,
                isVisible: args => args.storage.getValueByPath(showChildrenPath),
                rows: [[{
                    id: isThereOtherProviderPath
                }], [
                    { id: createPath(PrTaxpayerDeclarationEntity.OtherProvider, PrTaxpayerDeclarationOtherProviderEntity.FirstName) },
                    { id: createPath(PrTaxpayerDeclarationEntity.OtherProvider, PrTaxpayerDeclarationOtherProviderEntity.LastName) },
                    { id: createPath(PrTaxpayerDeclarationEntity.OtherProvider, PrTaxpayerDeclarationOtherProviderEntity.PersonalIdentificationNumber) }
                ], [
                    { id: createPath(PrTaxpayerDeclarationEntity.OtherProvider, PrTaxpayerDeclarationOtherProviderEntity.Address, PrOtherProviderAddressEntity.Street) },
                    { id: createPath(PrTaxpayerDeclarationEntity.OtherProvider, PrTaxpayerDeclarationOtherProviderEntity.Address, PrOtherProviderAddressEntity.City) },
                    { id: createPath(PrTaxpayerDeclarationEntity.OtherProvider, PrTaxpayerDeclarationOtherProviderEntity.Address, PrOtherProviderAddressEntity.PostalCode) },
                    { id: createPath(PrTaxpayerDeclarationEntity.OtherProvider, PrTaxpayerDeclarationOtherProviderEntity.Address, PrOtherProviderAddressEntity.Country) },
                ], [
                    { id: createPath(PrTaxpayerDeclarationEntity.OtherProvider, PrTaxpayerDeclarationOtherProviderEntity.IsApplyingTaxBenefits) }
                ], [
                    { id: createPath(PrTaxpayerDeclarationEntity.OtherProvider, PrTaxpayerDeclarationOtherProviderEntity.IsEmployed) }
                ], [
                    { id: createPath(PrTaxpayerDeclarationEntity.OtherProvider, PrTaxpayerDeclarationOtherProviderEntity.EmployerName) }
                ], [
                    { id: createPath(PrTaxpayerDeclarationEntity.OtherProvider, PrTaxpayerDeclarationOtherProviderEntity.EmployerAddress, PrOtherProviderEmployerAddressEntity.Street) },
                    { id: createPath(PrTaxpayerDeclarationEntity.OtherProvider, PrTaxpayerDeclarationOtherProviderEntity.EmployerAddress, PrOtherProviderEmployerAddressEntity.City) },
                    { id: createPath(PrTaxpayerDeclarationEntity.OtherProvider, PrTaxpayerDeclarationOtherProviderEntity.EmployerAddress, PrOtherProviderEmployerAddressEntity.PostalCode) },
                    { id: createPath(PrTaxpayerDeclarationEntity.OtherProvider, PrTaxpayerDeclarationOtherProviderEntity.EmployerAddress, PrOtherProviderEmployerAddressEntity.Country) }
                ]]
            }, {
                id: "disability",
                isVisible: ifAny(isVisibleForDiscountType(PrTaxDiscountTypeCode.BasicDisabilityDiscount), isVisibleForDiscountType(PrTaxDiscountTypeCode.ExtendedDisabilityDiscount)),
                title: i18next.t("TaxpayerDeclaration:Form.DisabilityDiscount"),
                togglePropPath: hasDisabilityDiscount,
                isSmallBottomMargin: true,
                customizationData: {
                    isLocked: true
                },
                rows: [[{ id: disabilityDiscountType }]]
            },
            getGroupForDeductionType(PrTaxDiscountTypeCode.DiscountForZTP_PHolder)
        ]
    };

    form.fieldDefinition[PrTaxpayerDeclarationEntity.Employee] = {
        ...employeeSelectDef,
        isVisible: !isSingleEmployeeView
    };

    form.groups = arrayInsert(form.groups, {
        id: "main",
        rows: [[{ id: PrTaxpayerDeclarationEntity.Employee }, { id: PrTaxpayerDeclarationEntity.Year }]]
    }, 0);

    return {
        entitySet: EntitySetName.PrTaxpayerDeclarations,
        table,
        form
    };
};

getDefinitions.translationFiles = ["TaxpayerDeclaration", "NumberRange"];

setDefByEntityType(EntityTypeName.PrTaxpayerDeclaration, getDefinitions);